import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ErrorHandler, NgModule } from '@angular/core';
import { ChunkErrorHandler } from './_helpers/chunk-error.handler';
import { RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  HttpClientModule,
} from '@angular/common/http';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HomeComponent } from './home/home.component';
import { authInterceptorProviders } from './_helpers/auth.interceptor';
import { CommonModule, registerLocaleData } from '@angular/common';
import { LoginModule } from './pages/login/login.module';
import { RegisterModule } from './pages/register/register.module';
import { RegisterFinancialLinkModule } from './pages/register-financial-link/register-financial-link.module';
import { AppCommonModule } from '@components/app.common.module';
import { CommonServicesModule } from './services/services.module';
import {
  TranslateModule,
  TranslateLoader,
  TranslateCompiler,
} from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { YearPlaceholderTranslateCompiler } from './_helpers/year-placeholder-translate.compiler';
import { InternalNotificationsModule } from './pages/internal-notifications/internal-notifications.module';
import { AuthReverseGuard } from './common/authReverse.guard';
import { HttpErrorInterceptor } from './common/common.http.error.interceptor';
import { MailshotModule } from './pages/mailshot/mailshot.module';
import { QuoteModule } from './pages/quote/quote.module';
import es from '@angular/common/locales/es';
import { ModalComponent } from '@components/modal/modal.component';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { DownloadsModule } from './pages/downloads/downloads.module';
import { HttpCacheInterceptor } from './services/cache';
import { TrackerService } from './services/tracker.service';
import { popperVariation, provideTippyConfig, provideTippyLoader, tooltipVariation } from '@ngneat/helipopper/config';
import { SESSION_RESETTABLES } from './common/session-resettable';
import { ActiveProfileService } from './common/active-profile.service';
import { OnboardingService } from './pages/onboarding/data-access/state';
import {
  EXCEL_ENGINE,
  REPORT_THEME,
  DEFAULT_CORPORATE_THEME,
} from '@app/services/reporting';
import { ExcelJsEngine } from '@app/services/reporting/infrastructure/exceljs/exceljs.engine';
import { environment } from '@env/environment.local';
import { IncidentReportMockInterceptor } from
  './services/mock/incident-report-mock.interceptor';

registerLocaleData(es);
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http as any);
}

@NgModule({
  declarations: [AppComponent, HomeComponent, ModalComponent],
  imports: [
    RouterModule,
    LoginModule,
    RegisterModule,
    RegisterFinancialLinkModule,
    InternalNotificationsModule,
    CommonModule,
    ReactiveFormsModule,
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    AppRoutingModule,
    FormsModule,
    AppCommonModule,
    CommonServicesModule,
    MailshotModule,
    QuoteModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: (http: HttpClient) => {
          return new TranslateHttpLoader(http, './assets/i18n/', `.json?v=${environment.version}`);
        },
        deps: [HttpClient],
      },
      compiler: {
        provide: TranslateCompiler,
        useClass: YearPlaceholderTranslateCompiler,
      },
    }),
    NgbModule,
    DownloadsModule
  ],

  providers: [
    { provide: ErrorHandler, useClass: ChunkErrorHandler },
    TrackerService,
    authInterceptorProviders,
    // TODO: Remove this mock interceptor manually before real backend
    // integration/release.
//     {
//       provide: HTTP_INTERCEPTORS,
//       useClass: IncidentReportMockInterceptor,
//       multi: true,
//     },
    AuthReverseGuard,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpCacheInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true,
    },
    provideTippyConfig({
      defaultVariation: 'tooltip',
      variations: {
        tooltip: tooltipVariation,
        popper: popperVariation,
      },
      duration:  [null, 0],
      delay: [0, 0]
    }),
    provideTippyLoader(() => import('tippy.js')),
    {
      provide: SESSION_RESETTABLES,
      useExisting: OnboardingService,
      multi: true,
    },
    {
      provide: SESSION_RESETTABLES,
      useExisting: ActiveProfileService,
      multi: true,
    },
    // Single swap point for the Excel reporting engine. Replacing the
    // useClass here is the entire migration path to another library.
    { provide: EXCEL_ENGINE, useClass: ExcelJsEngine },
    { provide: REPORT_THEME, useValue: DEFAULT_CORPORATE_THEME },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
